import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Save } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';
import { AdminPageToolbar } from '../components/admin/AdminLayout';
import { LoadingSpinner } from '../components/LoadingSpinner';

type Plan = {
  id: string;
  code: string;
  name: string;
  amount_paise: number;
  credits_per_period: number;
  is_paid: boolean;
  razorpay_plan_id: string | null;
  sort_order: number;
  is_active: boolean;
};

type SubRow = {
  id: string;
  hy_user_id: string;
  status: string;
  razorpay_subscription_id: string | null;
  plan_id: string;
  current_period_end: string | null;
  subscription_plans: { name: string; code: string } | { name: string; code: string }[];
};

type InvRow = {
  id: string;
  hy_user_id: string;
  amount_paise: number;
  status: string;
  paid_at: string | null;
  razorpay_invoice_id: string | null;
};

export function AdminBillingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subs, setSubs] = useState<SubRow[]>([]);
  const [invoices, setInvoices] = useState<InvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [settings, setSettings] = useState<{ subscriptions_enabled: boolean; is_live_razorpay: boolean } | null>(null);
  const [edits, setEdits] = useState<Record<string, Partial<Plan>>>({});

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;

  const load = async () => {
    setErr(null);
    setLoading(true);
    const p = await supabase.from('subscription_plans').select('*').order('sort_order', { ascending: true });
    const planData = (p.data as Plan[]) || [];
    if (p.error) {
      setErr(p.error.message);
    } else {
      setPlans(planData);
    }
    const planMap = new Map(planData.map((x) => [x.id, x.name as string]));
    const s = await supabase
      .from('user_subscriptions')
      .select('id, hy_user_id, status, razorpay_subscription_id, plan_id, current_period_end')
      .order('created_at', { ascending: false })
      .limit(100);
    if (!s.error && s.data) {
      setSubs(
        (s.data as { id: string; hy_user_id: string; status: string; razorpay_subscription_id: string | null; plan_id: string; current_period_end: string | null }[]).map(
          (r) => ({
            ...r,
            subscription_plans: { name: planMap.get(r.plan_id) || r.plan_id, code: '' },
          }),
        ),
      );
    }
    const i = await supabase
      .from('billing_invoices')
      .select('id, hy_user_id, amount_paise, status, paid_at, razorpay_invoice_id')
      .order('created_at', { ascending: false })
      .limit(100);
    if (!i.error && i.data) {
      setInvoices(i.data as InvRow[]);
    }
    const g = await supabase.from('app_billing_settings').select('subscriptions_enabled, is_live_razorpay').eq('id', 1).maybeSingle();
    if (!g.error && g.data) {
      setSettings(g.data as { subscriptions_enabled: boolean; is_live_razorpay: boolean });
    }
    setLoading(false);
  };

  useEffect(() => {
    const access = parseAdminStaffSession();
    if (!access) {
      navigate('/admin');
      return;
    }
    if (access.role !== 'superadmin') {
      navigate('/admin');
      return;
    }
    void load();
  }, [navigate]);

  const updateField = (id: string, field: keyof Plan, value: string | number | boolean | null) => {
    setEdits((e) => ({ ...e, [id]: { ...e[id], [field]: value } }));
  };

  const savePlan = async (id: string) => {
    const patch = edits[id];
    if (!patch || Object.keys(patch).length === 0) return;
    setSaving(id);
    setErr(null);
    const { error } = await supabase.from('subscription_plans').update(patch as never).eq('id', id);
    if (error) {
      setErr(error.message);
    } else {
      setEdits((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      });
      await load();
    }
    setSaving(null);
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving('settings');
    setErr(null);
    const { error } = await supabase
      .from('app_billing_settings')
      .update({
        subscriptions_enabled: settings.subscriptions_enabled,
        is_live_razorpay: settings.is_live_razorpay,
        updated_at: new Date().toISOString(),
      } as never)
      .eq('id', 1);
    if (error) setErr(error.message);
    setSaving(null);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
        <AdminPageToolbar title="Billing" subtitle="Plans, Razorpay, subscriptions" />
        <div className="flex flex-1 items-center justify-center p-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <AdminPageToolbar title="Billing" subtitle="Superadmin: subscription plans and support views" maxWidthClass="max-w-6xl" />
      <main className="mx-auto w-full min-h-0 max-w-6xl flex-1 overflow-y-auto overscroll-y-contain px-4 pb-8">
        {err ? (
          <div className="mb-4 rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
            {err}
          </div>
        ) : null}

        <section className="mb-8 rounded-2xl border border-subtle bg-elevated p-5">
          <div className="mb-3 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-th-secondary" />
            <h2 className="text-base font-semibold text-th-primary">App settings</h2>
          </div>
          {settings ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex items-center gap-2 text-sm text-th-secondary">
                <input
                  type="checkbox"
                  checked={settings.subscriptions_enabled}
                  onChange={(e) => setSettings({ ...settings, subscriptions_enabled: e.target.checked })}
                />
                Subscriptions enabled
              </label>
              <label className="flex items-center gap-2 text-sm text-th-secondary">
                <input
                  type="checkbox"
                  checked={settings.is_live_razorpay}
                  onChange={(e) => setSettings({ ...settings, is_live_razorpay: e.target.checked })}
                />
                Live Razorpay (vs test)
              </label>
              <button
                type="button"
                onClick={() => void saveSettings()}
                disabled={saving === 'settings'}
                className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-sm text-surface"
              >
                {saving === 'settings' ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                Save
              </button>
            </div>
          ) : null}
          <p className="mt-3 break-all text-xs text-th-tertiary">
            Webhook URL:{' '}
            <code className="text-th-secondary">
              {supabaseUrl.replace(/\/$/, '')}/functions/v1/razorpay-webhook
            </code>
          </p>
          <p className="mt-2 text-xs text-th-tertiary">
            Set <code>RAZORPAY_WEBHOOK_SECRET</code>, <code>RAZORPAY_KEY_ID</code>, <code>RAZORPAY_KEY_SECRET</code> in Edge
            secrets. Free-tier cron: <code>billing-free-renew</code> with <code>BILLING_FREE_RENEW_SECRET</code>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-base font-semibold text-th-primary">Plans</h2>
          <div className="overflow-x-auto rounded-xl border border-subtle">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="border-b border-subtle bg-surface/80 text-th-tertiary">
                <tr>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">₹ (paise)</th>
                  <th className="px-3 py-2">Credits / mo</th>
                  <th className="px-3 py-2">Razorpay plan id</th>
                  <th className="px-3 py-2">Active</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {plans.map((pl) => {
                  const e = edits[pl.id] || {};
                  const row = { ...pl, ...e } as Plan;
                  return (
                    <tr key={pl.id} className="border-b border-subtle/60 last:border-0">
                      <td className="px-3 py-2 font-mono text-xs text-th-tertiary">{pl.code}</td>
                      <td className="px-3 py-2">
                        <input
                          className="w-full max-w-[140px] rounded border border-subtle bg-elevated px-2 py-1 text-th-primary"
                          value={row.name}
                          onChange={(ev) => updateField(pl.id, 'name', ev.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className="w-24 rounded border border-subtle bg-elevated px-2 py-1 font-mono text-th-primary"
                          value={row.amount_paise}
                          onChange={(ev) => updateField(pl.id, 'amount_paise', Number(ev.target.value) || 0)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className="w-20 rounded border border-subtle bg-elevated px-2 py-1 font-mono text-th-primary"
                          value={row.credits_per_period}
                          onChange={(ev) => updateField(pl.id, 'credits_per_period', Number(ev.target.value) || 0)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className="w-full min-w-[180px] max-w-[220px] rounded border border-subtle bg-elevated px-2 py-1 font-mono text-xs text-th-primary"
                          value={row.razorpay_plan_id ?? ''}
                          onChange={(ev) => updateField(pl.id, 'razorpay_plan_id', ev.target.value || null)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={row.is_active}
                          onChange={(ev) => updateField(pl.id, 'is_active', ev.target.checked)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          disabled={!edits[pl.id] || saving === pl.id}
                          onClick={() => void savePlan(pl.id)}
                          className="text-brand hover:underline disabled:opacity-40"
                        >
                          {saving === pl.id ? '…' : 'Save row'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-base font-semibold text-th-primary">Recent subscriptions</h2>
          <div className="overflow-x-auto rounded-xl border border-subtle text-xs">
            <table className="w-full min-w-[640px] text-left">
              <thead className="border-b border-subtle bg-surface/80 text-th-tertiary">
                <tr>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2">Plan</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Razorpay sub</th>
                  <th className="px-2 py-2">Period end</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => {
                  const planName = Array.isArray(s.subscription_plans)
                    ? s.subscription_plans[0]?.name
                    : s.subscription_plans?.name;
                  return (
                    <tr key={s.id} className="border-b border-subtle/50">
                      <td className="px-2 py-1.5 font-mono text-th-secondary">{s.hy_user_id}</td>
                      <td className="px-2 py-1.5">{planName || s.plan_id}</td>
                      <td className="px-2 py-1.5">{s.status}</td>
                      <td className="px-2 py-1.5 font-mono">{s.razorpay_subscription_id || '—'}</td>
                      <td className="px-2 py-1.5">{s.current_period_end ? new Date(s.current_period_end).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-base font-semibold text-th-primary">Recent invoices</h2>
          <div className="overflow-x-auto rounded-xl border border-subtle text-xs">
            <table className="w-full min-w-[480px] text-left">
              <thead className="border-b border-subtle bg-surface/80 text-th-tertiary">
                <tr>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2">Amount</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((iv) => (
                  <tr key={iv.id} className="border-b border-subtle/50">
                    <td className="px-2 py-1.5 font-mono text-th-secondary">{iv.hy_user_id}</td>
                    <td className="px-2 py-1.5">₹{(iv.amount_paise / 100).toLocaleString('en-IN')}</td>
                    <td className="px-2 py-1.5">{iv.status}</td>
                    <td className="px-2 py-1.5 font-mono">{iv.razorpay_invoice_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
